import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router'
import { api } from '../servizi/api.ts'
import { useSessione } from '../servizi/sessione.tsx'
import {
  Bottone, Campo, Etichetta, Linguette, Modale, Paginazione, Riquadro, Scelta, Spunta, Tabella,
} from '../componenti/base.tsx'
import { Attesa, Avviso, Dato, Intestazione, Vuoto } from '../componenti/Pezzi.tsx'
import { LIMITE, conteggio, messaggioErrore, usaElenco } from '../componenti/elenco.tsx'
import { IconaIndietro, IconaPiu } from '../componenti/Icone.tsx'
import { SceltaComune, type ComuneScelto } from './Ente.tsx'
import { inOrdineDiSede } from '../servizi/sedi.ts'
import { OrariChiusure } from './sedi/OrariChiusure.tsx'
import { Stanze } from './sedi/Stanze.tsx'

/**
 * Sedi CIDIS: elenco e scheda dell'amministrazione (N-2, CAT-15).
 *
 * Finora le sedi si potevano solo creare. Portando qui la dozzina di record che erano sedi di CIDIS
 * registrate come enti, la sede diventa un'anagrafica: sede padre, ente che la ospita, progetti,
 * referenti, apertura e chiusura. Tutto sotto `sede.gestisci`: la sede e' il primo criterio del
 * perimetro, e chi la cambia sposta il perimetro di chi ci lavora.
 *
 * Dal 19/10/2026 (463) una sede con la sede madre e' uno sportello: chi ha un incarico sulla sede
 * madre lavora anche sui suoi sportelli. Un livello solo. La scheda di una sede elenca i suoi
 * sportelli e permette di collegarne, staccarne, rinominarli e chiuderli; la Direzione ha
 * `sede.gestisci` come l'amministrazione.
 *
 * Dal 3/11/2026 (556) la scheda ha la linguetta «Orari e chiusure». Chi ha `sede.agenda` senza
 * `sede.gestisci` (Project Manager e coordinatori) arriva qui dal menu e vede solo quella: l'elenco
 * delle sedi e' quello della sessione, la scheda la sola linguetta degli orari.
 */

/** Etichetta decisa per il tipo 'itinerante': «itinerante» da solo non diceva nulla agli operatori. */
export const TIPI_SEDE = [
  { valore: 'fissa', etichetta: 'Fissa' },
  { valore: 'itinerante', etichetta: 'Ambulante / presidio' },
] as const

export const nomeTipoSede = (tipo: string) =>
  TIPI_SEDE.find((t) => t.valore === tipo)?.etichetta ?? tipo

type RigaSede = {
  id: string; codice: string; nome: string; tipo: string; attiva: boolean
  comune: string | null; comune_nome: string | null; provincia_sigla: string | null
  indirizzo: string | null
  sede_padre_id: string | null; sede_padre_nome: string | null
  ente_ospitante: string | null; ente_ospitante_nome: string | null
  progetto_id: string | null; progetto_titolo: string | null; progetto_codice: string | null
  sedi_figlie: number; sportelli_attivi?: number; referenti: number
  progetti: { id: string; titolo: string; codice: string | null }[]
  da_ente_id: string | null; da_ente_nome: string | null
}

type ReferenteSede = {
  id: string; nome: string; cognome: string; ruolo: string | null; ufficio: string | null
  email: string | null; telefono: string | null; attivo: boolean; origine_referente_id: string | null
}

type DettaglioSede = RigaSede & {
  creata_il: string
  referenti: ReferenteSede[]
  sedi_figlie_elenco?: never
  progetti: { id: string; titolo: string; codice: string | null }[]
  da_enti: { stakeholder_id: string; nome_originale: string; regola: string; eseguita_il: string }[]
}

/** Sportello della sede, con quanto lo usa: schede, percorsi, servizi del registro. */
type SedeFiglia = {
  id: string; codice: string; nome: string; tipo: string; attiva: boolean; comune_nome: string | null
  schede: number; percorsi: number; servizi: number
}

/** Sede di GET /riferimenti/sedi?tutte=true, per le scelte della sede madre. */
type SedeDiRiferimento = {
  id: string; codice: string; nome: string; attiva: boolean
  sede_padre_id: string | null; sede_padre_nome: string | null
}

const luogo = (s: { comune_nome: string | null; provincia_sigla: string | null; indirizzo: string | null }) =>
  [s.indirizzo, s.comune_nome && `${s.comune_nome}${s.provincia_sigla ? ` (${s.provincia_sigla})` : ''}`]
    .filter(Boolean).join(' · ') || null

/** «3 sportelli», «3 sportelli, 1 chiuso». */
function contaSportelli(tutti: number, aperti = tutti): string {
  const chiusi = tutti - aperti
  return `${tutti === 1 ? '1 sportello' : `${tutti} sportelli`}`
    + (chiusi > 0 ? `, ${chiusi === 1 ? '1 chiuso' : `${chiusi} chiusi`}` : '')
}

// --- elenco --------------------------------------------------------------------------------------

export function Sedi() {
  const { puo } = useSessione()
  if (!puo('sede.gestisci')) return <SediPerOrari />
  return <SediAmministrazione />
}

/**
 * Per chi scrive orari e chiusure ma non gestisce le sedi: le sedi attive della sessione, ogni sede
 * seguita dai suoi sportelli, con il collegamento alla linguetta degli orari.
 */
function SediPerOrari() {
  const { sedi } = useSessione()
  return (
    <>
      <Intestazione titolo="Sedi CIDIS" sottotitolo="Orari e chiusure" />
      <Riquadro denso>
        <Tabella intestazioni={['Sede', '']} vuoto={<Vuoto testo="Nessuna sede." />}>
          {sedi.map((s) => (
            <tr key={s.id}>
              <td data-etichetta="Sede">
                <span className={`dato-testo${s.sede_padre_id ? ' sedi-sportello' : ''}`}>
                  <Link to={`/sedi/${s.id}`} className="voce-principale">{s.codice} — {s.nome}</Link>
                </span>
              </td>
              <td><Link to={`/sedi/${s.id}`}>Orari e chiusure</Link></td>
            </tr>
          ))}
        </Tabella>
      </Riquadro>
    </>
  )
}

function SediAmministrazione() {
  const { puo } = useSessione()
  const [parametri, setParametri] = useSearchParams()
  const [testo, setTesto] = useState(() => parametri.get('cerca') ?? '')
  const [creazione, setCreazione] = useState(false)

  const gestisce = puo('sede.gestisci')
  const aggiorna = useCallback((chiave: string, valore: string) => {
    setParametri((attuali) => {
      const nuovi = new URLSearchParams(attuali)
      if (valore) nuovi.set(chiave, valore); else nuovi.delete(chiave)
      return nuovi
    }, { replace: true })
  }, [setParametri])

  // Il testo passa nell'indirizzo dopo una pausa nella digitazione: una lettura per tasto non
  // serve a nessuno e riempie il registro eventi.
  const cercaInUso = parametri.get('cerca') ?? ''
  useEffect(() => {
    const termine = testo.trim()
    if (termine === cercaInUso) return
    const attesa = setTimeout(() => aggiorna('cerca', termine), 350)
    return () => clearTimeout(attesa)
  }, [testo, cercaInUso, aggiorna])

  const query = useMemo(() => {
    const q = new URLSearchParams()
    for (const chiave of ['cerca', 'attiva']) {
      const valore = parametri.get(chiave)
      if (valore) q.set(chiave, valore)
    }
    return q.toString()
  }, [parametri])
  const { dati, errore, ricarica, finestra } =
    usaElenco<RigaSede>(`/amministrazione/sedi${query ? `?${query}` : ''}`)

  return (
    <>
      <Intestazione
        titolo="Sedi CIDIS"
        sottotitolo={conteggio(finestra.totale, 'sede', 'sedi')}
        azioni={gestisce
          ? <Bottone variante="primario" icona={<IconaPiu />} onClick={() => setCreazione(true)}>Nuova sede</Bottone>
          : undefined}
      />

      <Riquadro titolo="Filtri">
        <div className="filtri-report">
          <Campo etichetta="Cerca" tipo="search" valore={testo} cambia={setTesto} segnaposto="Nome o codice" />
          <Scelta etichetta="Stato" valore={parametri.get('attiva') ?? ''}
                  cambia={(v) => aggiorna('attiva', v)}
                  opzioni={[{ valore: '', etichetta: 'Tutte' },
                    { valore: 'true', etichetta: 'Attive' },
                    { valore: 'false', etichetta: 'Chiuse' }]} />
        </div>
      </Riquadro>

      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {!errore && !dati && <Attesa />}
      {dati && (
        <Riquadro denso>
          <Tabella intestazioni={['Sede', 'Tipo', 'Luogo', 'Sportello di', 'Ospitata da', 'Stato']}
                   vuoto={<Vuoto testo="Nessuna sede con questi filtri." />}>
            {dati.map((s) => (
              <tr key={s.id}>
                <td data-etichetta="Sede">
                  <span className={`dato-testo${s.sede_padre_id ? ' sedi-sportello' : ''}`}>
                    <Link to={`/sedi/${s.id}`} className="voce-principale">{s.codice} — {s.nome}</Link>
                    {s.sedi_figlie > 0 && (
                      <span className="piccolo tenue">{contaSportelli(s.sedi_figlie, s.sportelli_attivi)}</span>
                    )}
                    {s.da_ente_nome && (
                      <span className="piccolo tenue">nata dall’ente «{s.da_ente_nome}»</span>
                    )}
                  </span>
                </td>
                <td data-etichetta="Tipo">{nomeTipoSede(s.tipo)}</td>
                <td data-etichetta="Luogo">{luogo(s) ?? <span className="tenue">—</span>}</td>
                <td data-etichetta="Sportello di">
                  {s.sede_padre_id
                    ? <Link to={`/sedi/${s.sede_padre_id}`}>{s.sede_padre_nome}</Link>
                    : <span className="tenue">—</span>}
                </td>
                <td data-etichetta="Ospitata da">
                  {s.ente_ospitante_nome ?? <span className="tenue">—</span>}
                </td>
                <td data-etichetta="Stato">
                  {s.attiva ? <Etichetta tono="positivo">attiva</Etichetta> : <Etichetta>chiusa</Etichetta>}
                </td>
              </tr>
            ))}
          </Tabella>
          <Paginazione salta={finestra.salta} limite={LIMITE} totale={finestra.totale}
                       ricevute={dati.length} cambia={finestra.cambia} />
        </Riquadro>
      )}

      {creazione && (
        <Modale titolo="Nuova sede" larga chiudi={() => setCreazione(false)}>
          <ModuloSede annulla={() => setCreazione(false)}
                      salvato={() => { setCreazione(false); ricarica() }} />
        </Modale>
      )}
    </>
  )
}

// --- scheda --------------------------------------------------------------------------------------

type LinguettaSede = 'anagrafica' | 'orari' | 'stanze'

/** La linguetta dall'indirizzo: `?linguetta=orari|stanze`; senza, la prima. */
function useLinguetta(prima: LinguettaSede) {
  const [parametri, setParametri] = useSearchParams()
  const chiesta = parametri.get('linguetta')
  const linguetta: LinguettaSede = chiesta === 'orari' || chiesta === 'stanze' ? chiesta : prima
  const scegli = (l: LinguettaSede) => setParametri((attuali) => {
    const nuovi = new URLSearchParams(attuali)
    if (l !== prima) nuovi.set('linguetta', l); else nuovi.delete('linguetta')
    return nuovi
  }, { replace: true })
  return [linguetta, scegli] as const
}

export function Sede() {
  const { id } = useParams()
  const { puo } = useSessione()
  if (!id) return null
  if (puo('sede.gestisci')) return <SedeAmministrazione id={id} />
  return <SedeAgenda id={id} />
}

/** Senza `sede.gestisci`: le linguette degli orari e delle stanze (558); il nome viene dalla sessione. */
function SedeAgenda({ id }: { id: string }) {
  const { sedi } = useSessione()
  const [linguetta, scegli] = useLinguetta('orari')
  const sede = sedi.find((x) => x.id === id)
  return (
    <>
      <Intestazione
        titolo={sede ? `${sede.codice} — ${sede.nome}` : 'Sede'}
        sottotitolo={linguetta === 'stanze' ? 'Stanze' : 'Orari e chiusure'}
        azioni={<Link className="bottone bottone-discreto" to="/sedi"><IconaIndietro /> Sedi</Link>}
      />
      <Linguette<LinguettaSede> etichetta="Sezioni della sede" attiva={linguetta} cambia={scegli}
                 voci={[{ valore: 'orari', etichetta: 'Orari e chiusure' }, { valore: 'stanze', etichetta: 'Stanze' }]} />
      {linguetta === 'stanze' ? <Stanze sedeId={id} /> : <OrariChiusure sedeId={id} />}
    </>
  )
}

function SedeAmministrazione({ id }: { id: string }) {
  const { puo } = useSessione()
  const [linguetta, scegli] = useLinguetta('anagrafica')
  const [sede, setSede] = useState<DettaglioSede | null>(null)
  const [figlie, setFiglie] = useState<SedeFiglia[]>([])
  const [errore, setErrore] = useState<string | null>(null)
  const [erroreAzione, setErroreAzione] = useState<string | null>(null)
  const [modifica, setModifica] = useState(false)
  const [referente, setReferente] = useState<ReferenteSede | 'nuovo' | null>(null)
  const [mostraDisattivati, setMostraDisattivati] = useState(false)
  const [inCorso, setInCorso] = useState<string | null>(null)

  const carica = useCallback(() => {
    setErrore(null)
    api.get<DettaglioSede & { sedi_figlie: SedeFiglia[] }>(`/amministrazione/sedi/${id}`)
      .then((s) => {
        setFiglie(s.sedi_figlie)
        setSede({ ...s, sedi_figlie: s.sedi_figlie.length })
      })
      .catch((e) => setErrore(messaggioErrore(e)))
  }, [id])
  useEffect(carica, [carica])

  async function esegui(chiave: string, azione: () => Promise<unknown>) {
    setErroreAzione(null)
    setInCorso(chiave)
    try {
      await azione()
      carica()
    } catch (e) {
      setErroreAzione(messaggioErrore(e))
    } finally {
      setInCorso(null)
    }
  }

  const gestisce = puo('sede.gestisci')
  const s = sede
  const disattivati = (s?.referenti ?? []).filter((r) => !r.attivo).length
  const referenti = (s?.referenti ?? []).filter((r) => mostraDisattivati || r.attivo)

  return (
    <>
      <Intestazione
        titolo={s ? `${s.codice} — ${s.nome}` : 'Sede'}
        sottotitolo={s ? nomeTipoSede(s.tipo) : undefined}
        azioni={<>
          <Link className="bottone bottone-discreto" to="/sedi"><IconaIndietro /> Sedi</Link>
          {s && gestisce && <Bottone variante="secondario" onClick={() => setModifica(true)}>Modifica</Bottone>}
          {s && gestisce && (s.attiva
            ? <Bottone variante="critico" inCorso={inCorso === 'sede'}
                       onClick={() => void esegui('sede', () =>
                         api.patch(`/amministrazione/sedi/${id}`, { attiva: false }))}>
                Chiudi la sede
              </Bottone>
            : <Bottone variante="secondario" inCorso={inCorso === 'sede'}
                       onClick={() => void esegui('sede', () =>
                         api.patch(`/amministrazione/sedi/${id}`, { attiva: true }))}>
                Riapri la sede
              </Bottone>)}
        </>}
      />

      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {!errore && !s && <Attesa />}
      {erroreAzione && <Avviso tipo="errore">{erroreAzione}</Avviso>}

      {s && (
        <Linguette<LinguettaSede> etichetta="Sezioni della sede" attiva={linguetta} cambia={scegli}
                   voci={[{ valore: 'anagrafica', etichetta: 'Anagrafica' }, { valore: 'orari', etichetta: 'Orari e chiusure' },
                     { valore: 'stanze', etichetta: 'Stanze' }]} />
      )}
      {s && linguetta === 'orari' && <OrariChiusure sedeId={s.id} />}
      {s && linguetta === 'stanze' && <Stanze sedeId={s.id} />}

      {s && linguetta === 'anagrafica' && (
        <>
          {!s.attiva && (
            <Avviso tipo="attenzione">
              <strong>Sede chiusa.</strong> Non si può più scegliere nelle tendine; i dati che la
              indicano restano consultabili.
            </Avviso>
          )}
          {s.da_enti.length > 0 && (
            <Avviso tipo="informazione">
              <strong>Sede nata dall’anagrafica degli enti.</strong> Qui sono confluiti i record che
              registravano questa sede come se fosse un ente:{' '}
              {s.da_enti.map((d, i) => (
                <span key={d.stakeholder_id}>
                  {i > 0 && ', '}
                  <Link to={`/enti/${d.stakeholder_id}`}>{d.nome_originale}</Link>
                </span>
              ))}. Gli enti restano consultabili come storico.
            </Avviso>
          )}

          <div className="sedi-dettaglio">
            <Riquadro titolo="Anagrafica">
              <dl className="sedi-dati">
                <Dato nome="Codice">{s.codice}</Dato>
                <Dato nome="Tipo">{nomeTipoSede(s.tipo)}</Dato>
                <Dato nome="Luogo">{luogo(s) ?? <span className="tenue">non indicato</span>}</Dato>
                <Dato nome="Sportello di">
                  {s.sede_padre_id
                    ? <Link to={`/sedi/${s.sede_padre_id}`}>{s.sede_padre_nome}</Link>
                    : <span className="tenue">nessuna sede: è una sede</span>}
                </Dato>
                <Dato nome="Ente ospitante">
                  {s.ente_ospitante
                    ? <Link to={`/enti/${s.ente_ospitante}`}>{s.ente_ospitante_nome}</Link>
                    : <span className="tenue">nessuno</span>}
                </Dato>
                <Dato nome="Progetto di riferimento">
                  {s.progetto_id
                    ? <Link to={`/progetti/${s.progetto_id}`}>{s.progetto_titolo}</Link>
                    : <span className="tenue">nessuno</span>}
                </Dato>
              </dl>
            </Riquadro>

            <Riquadro titolo="Progetti collegati">
              {s.progetti.length
                ? <ul className="sedi-elenco">
                    {s.progetti.map((p) => (
                      <li key={p.id}>
                        <Link to={`/progetti/${p.id}`}>{p.codice ? `${p.codice} — ` : ''}{p.titolo}</Link>
                      </li>
                    ))}
                  </ul>
                : <p className="tenue">Nessuno.</p>}
            </Riquadro>
          </div>

          <Sportelli sede={s} sportelli={figlie} gestisce={gestisce} ricarica={carica} />

          <Riquadro
            titolo="Referenti della sede"
            azioni={<>
              {disattivati > 0 && (
                <Spunta etichetta={`Mostra i disattivati (${disattivati})`} valore={mostraDisattivati}
                        cambia={setMostraDisattivati} />
              )}
              {gestisce && (
                <Bottone variante="secondario" dimensione="piccolo" onClick={() => setReferente('nuovo')}>
                  Aggiungi referente
                </Bottone>
              )}
            </>}
            denso
          >
            <Tabella intestazioni={['Referente', 'Ruolo', 'Contatti', 'Origine', '']}
                     vuoto={<Vuoto testo="Nessun referente registrato per questa sede." />}>
              {referenti.map((r) => (
                <tr key={r.id}>
                  <td data-etichetta="Referente">
                    {r.cognome} {r.nome}
                    {!r.attivo && <> <Etichetta>disattivato</Etichetta></>}
                  </td>
                  <td data-etichetta="Ruolo">
                    {[r.ruolo, r.ufficio].filter(Boolean).join(' · ') || <span className="tenue">—</span>}
                  </td>
                  <td data-etichetta="Contatti" className="piccolo">
                    {[r.email, r.telefono].filter(Boolean).join(' · ') || '—'}
                  </td>
                  <td data-etichetta="Origine" className="piccolo tenue">
                    {r.origine_referente_id ? 'copiato dall’ente' : 'inserito a mano'}
                  </td>
                  <td>
                    {gestisce && (
                      <span className="riga">
                        <Bottone variante="discreto" dimensione="piccolo"
                                 onClick={() => setReferente(r)}>Modifica</Bottone>
                        <Bottone variante="discreto" dimensione="piccolo" inCorso={inCorso === r.id}
                                 onClick={() => void esegui(r.id, () =>
                                   api.patch(`/amministrazione/sedi/${id}/referenti/${r.id}`,
                                     { attivo: !r.attivo }))}>
                          {r.attivo ? 'Disattiva' : 'Riattiva'}
                        </Bottone>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </Tabella>
          </Riquadro>
        </>
      )}

      {modifica && s && (
        <Modale titolo="Modifica la sede" larga chiudi={() => setModifica(false)}>
          <ModuloSede iniziale={s} annulla={() => setModifica(false)}
                      salvato={() => { setModifica(false); carica() }} />
        </Modale>
      )}
      {referente && s && (
        <Modale titolo={referente === 'nuovo' ? 'Nuovo referente' : 'Modifica il referente'}
                chiudi={() => setReferente(null)}>
          <ModuloReferente sedeId={s.id} iniziale={referente === 'nuovo' ? undefined : referente}
                           annulla={() => setReferente(null)}
                           salvato={() => { setReferente(null); carica() }} />
        </Modale>
      )}
    </>
  )
}

// --- sportelli ----------------------------------------------------------------------------------

type AzioneSportello =
  | { tipo: 'collega' } | { tipo: 'nuovo' }
  | { tipo: 'rinomina'; sportello: SedeFiglia } | { tipo: 'stacca'; sportello: SedeFiglia }

/**
 * Sportelli di una sede (463): presidi, SAI, sportelli comunali collegati alla sede madre. Chi ha
 * un incarico sulla sede madre lavora anche su di loro; per questo collegare e staccare uno
 * sportello dice prima che cosa cambia per chi ci lavora.
 */
function Sportelli({ sede, sportelli, gestisce, ricarica }: {
  sede: DettaglioSede; sportelli: SedeFiglia[]; gestisce: boolean; ricarica: () => void
}) {
  const [azione, setAzione] = useState<AzioneSportello | null>(null)
  const [inCorso, setInCorso] = useState<string | null>(null)
  const [errore, setErrore] = useState<string | null>(null)

  async function cambia(id: string, corpo: Record<string, unknown>) {
    setErrore(null)
    setInCorso(id)
    try {
      await api.patch(`/amministrazione/sedi/${id}`, corpo)
      ricarica()
    } catch (e) {
      setErrore(messaggioErrore(e))
    } finally {
      setInCorso(null)
    }
  }

  if (sede.sede_padre_id) {
    return (
      <Riquadro titolo="Sportelli">
        <p>
          Questa sede è uno sportello di{' '}
          <Link to={`/sedi/${sede.sede_padre_id}`}>{sede.sede_padre_nome}</Link>: uno sportello non ha
          sportelli suoi. Chi ha un incarico su «{sede.sede_padre_nome}» vede e lavora anche le schede, i
          percorsi, gli interventi e i servizi di questo sportello; chi ha un incarico su questo sportello
          vede solo i suoi.
        </p>
      </Riquadro>
    )
  }

  return (
    <Riquadro
      titolo="Sportelli"
      azioni={gestisce && sede.attiva && (
        <>
          <Bottone variante="secondario" dimensione="piccolo" onClick={() => setAzione({ tipo: 'collega' })}>
            Collega uno sportello
          </Bottone>
          <Bottone variante="secondario" dimensione="piccolo" icona={<IconaPiu />}
                   onClick={() => setAzione({ tipo: 'nuovo' })}>
            Nuovo sportello
          </Bottone>
        </>
      )}
      denso
    >
      <p className="tenue sedi-nota">
        Chi ha un incarico su questa sede vede e lavora anche le schede, i percorsi, gli interventi e i
        servizi dei suoi sportelli. Chi ha un incarico su uno sportello vede solo quello.
      </p>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      <Tabella intestazioni={['Sportello', 'Luogo', 'Schede', 'Percorsi', 'Servizi', 'Stato', '']}
               vuoto={<Vuoto testo="Nessuno sportello collegato a questa sede." />}>
        {sportelli.map((f) => (
          <tr key={f.id}>
            <td data-etichetta="Sportello">
              <Link to={`/sedi/${f.id}`}>{f.codice} — {f.nome}</Link>
              <span className="piccolo tenue"> · {nomeTipoSede(f.tipo)}</span>
            </td>
            <td data-etichetta="Luogo">{f.comune_nome ?? <span className="tenue">—</span>}</td>
            <td data-etichetta="Schede" className="mono a-destra">{f.schede}</td>
            <td data-etichetta="Percorsi" className="mono a-destra">{f.percorsi}</td>
            <td data-etichetta="Servizi" className="mono a-destra">{f.servizi}</td>
            <td data-etichetta="Stato">
              {f.attiva ? <Etichetta tono="positivo">aperto</Etichetta> : <Etichetta>chiuso</Etichetta>}
            </td>
            <td>
              {gestisce && (
                <span className="riga">
                  <Bottone variante="discreto" dimensione="piccolo"
                           onClick={() => setAzione({ tipo: 'rinomina', sportello: f })}>Rinomina</Bottone>
                  <Bottone variante="discreto" dimensione="piccolo" inCorso={inCorso === f.id}
                           onClick={() => void cambia(f.id, { attiva: !f.attiva })}>
                    {f.attiva ? 'Chiudi' : 'Riapri'}
                  </Bottone>
                  <Bottone variante="discreto" dimensione="piccolo"
                           onClick={() => setAzione({ tipo: 'stacca', sportello: f })}>Stacca</Bottone>
                </span>
              )}
            </td>
          </tr>
        ))}
      </Tabella>

      {azione?.tipo === 'collega' && (
        <Modale titolo={`Collega uno sportello a «${sede.nome}»`} chiudi={() => setAzione(null)}>
          <CollegaSportello sede={sede} annulla={() => setAzione(null)}
                            fatto={() => { setAzione(null); ricarica() }} />
        </Modale>
      )}
      {azione?.tipo === 'nuovo' && (
        <Modale titolo={`Nuovo sportello di «${sede.nome}»`} larga chiudi={() => setAzione(null)}>
          <ModuloSede madre={sede.id} annulla={() => setAzione(null)}
                      salvato={() => { setAzione(null); ricarica() }} />
        </Modale>
      )}
      {azione?.tipo === 'rinomina' && (
        <Modale titolo="Rinomina lo sportello" chiudi={() => setAzione(null)}>
          <RinominaSede sede={azione.sportello} annulla={() => setAzione(null)}
                        fatto={() => { setAzione(null); ricarica() }} />
        </Modale>
      )}
      {azione?.tipo === 'stacca' && (
        <Modale titolo="Stacca lo sportello" chiudi={() => setAzione(null)} fuocoIniziale="azioni"
                azioni={<>
                  <Bottone variante="discreto" onClick={() => setAzione(null)}>Annulla</Bottone>
                  <Bottone variante="critico" inCorso={inCorso === azione.sportello.id}
                           onClick={() => void cambia(azione.sportello.id, { sede_padre_id: null })
                             .then(() => setAzione(null))}>
                    Stacca
                  </Bottone>
                </>}>
          <p>
            «{azione.sportello.nome}» non sarà più uno sportello di «{sede.nome}» e diventerà una sede a sé.
            Chi ha un incarico su «{sede.nome}» non ne vedrà più le schede, i percorsi, gli interventi e i
            servizi, a meno che non li veda per un altro incarico.
          </p>
        </Modale>
      )}
    </Riquadro>
  )
}

/**
 * Scelta della sede da collegare come sportello. Si propongono le sedi aperte senza sportelli
 * propri (un livello solo); una sede che e' gia' sportello di un'altra passa a questa, e lo si dice.
 */
function CollegaSportello({ sede, annulla, fatto }: {
  sede: DettaglioSede; annulla: () => void; fatto: () => void
}) {
  const [sedi, setSedi] = useState<SedeDiRiferimento[] | null>(null)
  const [scelta, setScelta] = useState('')
  const [errore, setErrore] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState(false)
  useEffect(() => {
    api.get<SedeDiRiferimento[]>('/riferimenti/sedi?tutte=true')
      .then(setSedi).catch((e) => setErrore(messaggioErrore(e)))
  }, [])

  const madri = new Set((sedi ?? []).map((x) => x.sede_padre_id).filter(Boolean))
  const candidate = (sedi ?? []).filter((x) => x.attiva && x.id !== sede.id
    && x.sede_padre_id !== sede.id && !madri.has(x.id))
  const scelto = candidate.find((x) => x.id === scelta)

  async function collega() {
    if (!scelta) { setErrore('Scegli la sede da collegare.'); return }
    setErrore(null)
    setInCorso(true)
    try {
      await api.patch(`/amministrazione/sedi/${scelta}`, { sede_padre_id: sede.id })
      fatto()
    } catch (e) {
      setErrore(messaggioErrore(e))
    } finally {
      setInCorso(false)
    }
  }

  if (!sedi && !errore) return <Attesa />
  return (
    <form onSubmit={(e) => { e.preventDefault(); void collega() }} noValidate>
      <Scelta etichetta="Sede da collegare" obbligatorio vuota="— seleziona —" valore={scelta} cambia={setScelta}
              opzioni={inOrdineDiSede(candidate).map((x) => ({
                valore: x.id,
                etichetta: x.sede_padre_id ? `${x.nome} (oggi sportello di ${x.sede_padre_nome})` : x.nome,
              }))}
              aiuto="Non compaiono le sedi chiuse e quelle che hanno sportelli propri." />
      {scelto && (
        <p className="tenue">
          Chi ha un incarico su «{sede.nome}» vedrà e lavorerà anche le schede, i percorsi, gli interventi
          e i servizi di «{scelto.nome}».
          {scelto.sede_padre_id && <> Chi ha un incarico su «{scelto.sede_padre_nome}» non li vedrà più,
            a meno che non li veda per un altro incarico.</>}
        </p>
      )}
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      <div className="azioni-modulo">
        <Bottone variante="discreto" onClick={annulla}>Annulla</Bottone>
        <Bottone variante="primario" tipo="submit" inCorso={inCorso}>Collega</Bottone>
      </div>
    </form>
  )
}

/** Solo il nome: il resto della scheda dello sportello si cambia dalla sua pagina. */
function RinominaSede({ sede, annulla, fatto }: {
  sede: { id: string; nome: string }; annulla: () => void; fatto: () => void
}) {
  const [nome, setNome] = useState(sede.nome)
  const [errore, setErrore] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState(false)
  async function salva() {
    if (nome.trim().length < 2) { setErrore('Il nome deve avere almeno due caratteri.'); return }
    setErrore(null)
    setInCorso(true)
    try {
      await api.patch(`/amministrazione/sedi/${sede.id}`, { nome: nome.trim() })
      fatto()
    } catch (e) {
      setErrore(messaggioErrore(e))
    } finally {
      setInCorso(false)
    }
  }
  return (
    <form onSubmit={(e) => { e.preventDefault(); void salva() }} noValidate>
      <Campo etichetta="Nome" obbligatorio maxLength={200} valore={nome} cambia={setNome} />
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      <div className="azioni-modulo">
        <Bottone variante="discreto" onClick={annulla}>Annulla</Bottone>
        <Bottone variante="primario" tipo="submit" inCorso={inCorso}>Salva il nome</Bottone>
      </div>
    </form>
  )
}

// --- moduli ---------------------------------------------------------------------------------------

type EnteOspitante = { id: string; sigla: string | null; nome: string }

/** Ente ospitante cercato per nome: l'elenco degli enti e' lungo e non sta in una tendina. */
function SceltaEnteOspitante({ valore, nome, cambia }: {
  valore: string; nome: string | null; cambia: (id: string, nome: string | null) => void
}) {
  const [testo, setTesto] = useState('')
  const [trovati, setTrovati] = useState<EnteOspitante[]>([])
  useEffect(() => {
    const termine = testo.trim()
    if (termine.length < 2) { setTrovati([]); return }
    const attesa = setTimeout(() => {
      api.get<EnteOspitante[]>(`/amministrazione/enti-ospitanti?cerca=${encodeURIComponent(termine)}`)
        .then(setTrovati).catch(() => setTrovati([]))
    }, 300)
    return () => clearTimeout(attesa)
  }, [testo])

  const etichetta = (e: EnteOspitante) => `${e.sigla ? `${e.sigla} — ` : ''}${e.nome}`
  // L'ente gia' registrato resta scegliibile anche quando la ricerca non lo riporta.
  const candidati = valore && !trovati.some((e) => e.id === valore)
    ? [{ id: valore, sigla: null, nome: nome ?? 'ente collegato' }, ...trovati]
    : trovati
  return (
    <div className="rete-ricerca rete-ricerca-piena">
      <Campo etichetta="Cerca l’ente ospitante" tipo="search" valore={testo} cambia={setTesto}
             segnaposto="Almeno due lettere del nome" />
      <Scelta etichetta="Ente ospitante" vuota="— nessuno —" valore={valore}
              opzioni={candidati.map((e) => ({ valore: e.id, etichetta: etichetta(e) }))}
              cambia={(id) => cambia(id, candidati.find((e) => e.id === id)?.nome ?? null)}
              aiuto="Gli enti diventati sedi CIDIS non compaiono: una sede non ospita se stessa." />
    </div>
  )
}

export function ModuloSede({ iniziale, madre, annulla, salvato }: {
  iniziale?: DettaglioSede
  /** Per un nuovo sportello: la sede madre gia' scelta. */
  madre?: string
  annulla: () => void; salvato: (id: string) => void
}) {
  const [codice, setCodice] = useState(iniziale?.codice ?? '')
  const [nome, setNome] = useState(iniziale?.nome ?? '')
  // Uno sportello nuovo parte come presidio, come gli sportelli ambulanti del vecchio archivio.
  const [tipo, setTipo] = useState(iniziale?.tipo ?? (madre ? 'itinerante' : 'fissa'))
  const [sedePadre, setSedePadre] = useState(iniziale?.sede_padre_id ?? madre ?? '')
  const [comune, setComune] = useState<ComuneScelto | null>(
    iniziale?.comune
      ? { codice_istat: iniziale.comune, nome: iniziale.comune_nome ?? iniziale.comune,
          provincia_sigla: iniziale.provincia_sigla }
      : null)
  const [indirizzo, setIndirizzo] = useState(iniziale?.indirizzo ?? '')
  const [ospitante, setOspitante] = useState(iniziale?.ente_ospitante ?? '')
  const [ospitanteNome, setOspitanteNome] = useState(iniziale?.ente_ospitante_nome ?? null)
  const [sedi, setSedi] = useState<SedeDiRiferimento[]>([])
  const [errore, setErrore] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState(false)

  // Le sedi madri comprendono le chiuse: una sede storica puo' dipendere da una sede non piu' in uso.
  // Uno sportello non ha sportelli (463): fra le sedi madri non compaiono gli sportelli.
  useEffect(() => {
    api.get<SedeDiRiferimento[]>('/riferimenti/sedi?tutte=true')
      .then(setSedi).catch(() => setSedi([]))
  }, [])
  const haSportelli = (iniziale?.sedi_figlie ?? 0) > 0
  const madri = sedi.filter((x) => x.id !== iniziale?.id && (!x.sede_padre_id || x.id === sedePadre))

  async function salva() {
    setErrore(null)
    setInCorso(true)
    const corpo = {
      codice: codice.trim(), nome: nome.trim(), tipo,
      sede_padre_id: sedePadre || null,
      comune: comune?.codice_istat ?? null,
      indirizzo: indirizzo.trim() || null,
      ente_ospitante: ospitante || null,
    }
    try {
      if (iniziale) {
        await api.patch(`/amministrazione/sedi/${iniziale.id}`, corpo)
        salvato(iniziale.id)
      } else {
        const creata = await api.post<{ id: string }>('/amministrazione/sedi', corpo)
        salvato(creata.id)
      }
    } catch (e) {
      setErrore(messaggioErrore(e))
    } finally {
      setInCorso(false)
    }
  }

  return (
    <form onSubmit={(e) => { e.preventDefault(); void salva() }} noValidate>
      <div className="griglia-modulo">
        <Campo etichetta="Codice" obbligatorio maxLength={30} valore={codice} cambia={setCodice}
               aiuto="Breve e stabile: compare negli elenchi e nelle esportazioni." />
        <Campo etichetta="Nome" obbligatorio maxLength={200} valore={nome} cambia={setNome} />
        <Scelta etichetta="Tipo" obbligatorio valore={tipo} cambia={setTipo}
                opzioni={TIPI_SEDE.map((t) => ({ valore: t.valore, etichetta: t.etichetta }))} />
        <Scelta etichetta="Sportello di" vuota="— nessuna: è una sede —" valore={sedePadre} cambia={setSedePadre}
                disabilitato={haSportelli}
                opzioni={madri.map((x) => ({ valore: x.id, etichetta: x.attiva ? x.nome : `${x.nome} (chiusa)` }))}
                aiuto={haSportelli
                  ? 'Ha degli sportelli: non può diventare a sua volta uno sportello.'
                  : 'Se è uno sportello, la sede a cui appartiene: chi lavora su quella sede vede anche questo.'} />
        <Campo etichetta="Indirizzo" maxLength={300} valore={indirizzo} cambia={setIndirizzo}
               aiuto="Via, numero civico e CAP: con il comune compare nella firma delle email di chi ha qui la sede principale." />
      </div>
      <SceltaComune etichetta="Comune" vuota="— nessuno —" valore={comune} cambia={setComune} />
      <SceltaEnteOspitante valore={ospitante} nome={ospitanteNome}
                           cambia={(id, n) => { setOspitante(id); setOspitanteNome(n) }} />

      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      <div className="azioni-modulo">
        <Bottone variante="discreto" onClick={annulla}>Annulla</Bottone>
        <Bottone variante="primario" tipo="submit" inCorso={inCorso}>
          {iniziale ? 'Salva le modifiche' : 'Crea la sede'}
        </Bottone>
      </div>
    </form>
  )
}

function ModuloReferente({ sedeId, iniziale, annulla, salvato }: {
  sedeId: string; iniziale?: ReferenteSede; annulla: () => void; salvato: () => void
}) {
  const [nome, setNome] = useState(iniziale?.nome ?? '')
  const [cognome, setCognome] = useState(iniziale?.cognome ?? '')
  const [ruolo, setRuolo] = useState(iniziale?.ruolo ?? '')
  const [ufficio, setUfficio] = useState(iniziale?.ufficio ?? '')
  const [email, setEmail] = useState(iniziale?.email ?? '')
  const [telefono, setTelefono] = useState(iniziale?.telefono ?? '')
  const [errore, setErrore] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState(false)

  async function salva() {
    setErrore(null)
    setInCorso(true)
    const corpo = {
      nome: nome.trim(), cognome: cognome.trim(), ruolo: ruolo.trim() || null,
      ufficio: ufficio.trim() || null, email: email.trim() || null, telefono: telefono.trim() || null,
    }
    try {
      if (iniziale) await api.patch(`/amministrazione/sedi/${sedeId}/referenti/${iniziale.id}`, corpo)
      else await api.post(`/amministrazione/sedi/${sedeId}/referenti`, corpo)
      salvato()
    } catch (e) {
      setErrore(messaggioErrore(e))
    } finally {
      setInCorso(false)
    }
  }

  return (
    <form onSubmit={(e) => { e.preventDefault(); void salva() }} noValidate>
      <div className="griglia-modulo">
        <Campo etichetta="Nome" obbligatorio maxLength={200} valore={nome} cambia={setNome} />
        <Campo etichetta="Cognome" obbligatorio maxLength={200} valore={cognome} cambia={setCognome} />
        <Campo etichetta="Ruolo" maxLength={120} valore={ruolo} cambia={setRuolo} />
        <Campo etichetta="Ufficio" maxLength={120} valore={ufficio} cambia={setUfficio} />
        <Campo etichetta="Email" tipo="email" maxLength={200} valore={email} cambia={setEmail} />
        <Campo etichetta="Telefono" tipo="tel" maxLength={40} valore={telefono} cambia={setTelefono} />
      </div>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      <div className="azioni-modulo">
        <Bottone variante="discreto" onClick={annulla}>Annulla</Bottone>
        <Bottone variante="primario" tipo="submit" inCorso={inCorso}>
          {iniziale ? 'Salva le modifiche' : 'Aggiungi il referente'}
        </Bottone>
      </div>
    </form>
  )
}
